/**
 * Cohort-level payment summary card (MED-519 / E13-7 AC11).
 * Shows total confirmed, paid, remaining, next date, and progress.
 */

import { useState, useEffect } from 'react';
import { Paper, Group, Text, Stack, Progress, Badge, SimpleGrid, Loader } from '@mantine/core';
import { cohortBillcomApi } from '../../../../services/api/billcom';
import type { CohortPaymentSummary } from '../../../../services/api/billcom';
import { formatCents } from '../../../../utils/formatters';
import { parseDateOnly } from '../../../../utils/dateHelpers';
import {
  COMPENSATION_STATUS_CONFIG,
  PAYMENT_STATUS_CONFIG,
} from '../../../../config/compensationConfig';

interface PaymentSummaryCardProps {
  cohortId: string;
  refreshKey?: number;
}

export function PaymentSummaryCard({ cohortId, refreshKey }: PaymentSummaryCardProps) {
  const [summary, setSummary] = useState<CohortPaymentSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await cohortBillcomApi.getPaymentSummary(cohortId);
        setSummary(data);
      } catch {
        // Silently fail
      } finally {
        setLoading(false);
      }
    })();
  }, [cohortId, refreshKey]);

  if (loading) {
    return (
      <Paper p="md" radius="md" style={{ border: '1px solid #E8E6E1', background: 'white' }}>
        <Group justify="center" py="sm">
          <Loader size="sm" />
        </Group>
      </Paper>
    );
  }

  if (!summary) return null;

  // MED-1286: `failed` belongs in this check. Without it a cohort whose only
  // payments have failed rendered nothing at all — no card, no badge, no hint
  // that thousands were outstanding.
  // MED-1306 / MDF-174: `awaiting_approval` belongs here for the same reason, and
  // it is now the FIRST state every payment passes through. Omitting it hid the
  // whole card for any cohort that had just been submitted.
  const relevantCount =
    summary.counts.pending +
    // `?? 0` is load-bearing. During a rolling deploy the frontend can read a response
    // from an API pod that predates this field. One undefined makes the whole sum NaN,
    // `NaN > 0` is false, and the card vanishes: the exact failure MED-1286 fixed.
    (summary.counts.awaiting_approval ?? 0) +
    // MED-1383: same `?? 0` reason as awaiting_approval.
    (summary.counts.approved ?? 0) +
    summary.counts.scheduled +
    summary.counts.processing +
    summary.counts.paid +
    summary.counts.failed;
  if (relevantCount === 0) return null;

  // MED-1286 (AC5): the bar is paid over owed, using the same definition as the
  // figures below it. total_confirmed_cents already excludes voided server-side,
  // so it is the correct denominator. (This replaced a no-op that subtracted
  // zero either way and left the bar disagreeing with the numbers.)
  const progressPct =
    summary.total_confirmed_cents > 0
      ? Math.round((summary.total_paid_cents / summary.total_confirmed_cents) * 100)
      : 0;

  const statusCfg = summary.aggregate_status
    ? COMPENSATION_STATUS_CONFIG[summary.aggregate_status]
    : null;

  // MED-1286 (AC4): process_date is a SQL DATE, so a raw new Date() renders the day
  // BEFORE for any viewer west of UTC. parseDateOnly keeps the calendar date.
  // MED-1383: the API now sends the Bill.com scheduled date. With no scheduled
  // payment it sends null, and the Next Payment figure is hidden, not shown as "--".
  const nextPayment = parseDateOnly(summary.next_payment_date);

  return (
    <Paper p="lg" radius="md" style={{ border: '1px solid #E8E6E1', background: 'white' }}>
      <Group justify="space-between" mb="md">
        <Text fw={700} size="lg">
          Payment Progress
        </Text>
        {statusCfg && (
          <Badge color={statusCfg.color} variant={statusCfg.variant} size="lg">
            {statusCfg.label}
          </Badge>
        )}
      </Group>

      <Progress value={progressPct} size="lg" color="medleyGreen" radius="xl" mb="md" />

      <SimpleGrid cols={nextPayment ? 4 : 3} spacing="md">
        <Stack gap={2}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Total Confirmed
          </Text>
          <Text size="lg" fw={700}>
            {formatCents(summary.total_confirmed_cents)}
          </Text>
        </Stack>
        <Stack gap={2}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Paid
          </Text>
          <Text size="lg" fw={700} c="medleyGreen">
            {formatCents(summary.total_paid_cents)}
          </Text>
        </Stack>
        <Stack gap={2}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Remaining
          </Text>
          <Text size="lg" fw={700}>
            {formatCents(summary.total_remaining_cents)}
          </Text>
        </Stack>
        {nextPayment && (
          <Stack gap={2}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Next Payment
            </Text>
            <Text size="lg" fw={700}>
              {nextPayment.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </Text>
          </Stack>
        )}
      </SimpleGrid>

      {((summary.counts.awaiting_approval ?? 0) > 0 ||
        (summary.counts.approved ?? 0) > 0 ||
        summary.counts.scheduled > 0 ||
        summary.counts.failed > 0 ||
        summary.counts.voided > 0) && (
        <Group mt="md" gap="sm">
          {/* MED-1306 / MDF-174: bills exist in Bill.com and no human has approved
              them. No dollar figure here on purpose: unlike failed money, this money
              is already counted in Remaining above, and awaiting approval is the
              normal state of a fresh submission. The count is the signal, because it
              says how many coaches are waiting on an approver. Per-payment amounts
              are in the list below. */}
          {(summary.counts.awaiting_approval ?? 0) > 0 && (
            <Badge color="warningAmber" variant="light" size="sm">
              {summary.counts.awaiting_approval} Awaiting approval
            </Badge>
          )}
          {/* MED-1383: approved in Bill.com, but no payment is scheduled there yet. */}
          {(summary.counts.approved ?? 0) > 0 && (
            <Badge
              color={PAYMENT_STATUS_CONFIG.approved.color}
              variant={PAYMENT_STATUS_CONFIG.approved.variant}
              size="sm"
            >
              {summary.counts.approved} {PAYMENT_STATUS_CONFIG.approved.label}
            </Badge>
          )}
          {/* MED-1383: a person scheduled the payment in Bill.com. Without this badge a
              scheduled bill showed nowhere on the card. Next Payment above gives its date. */}
          {summary.counts.scheduled > 0 && (
            <Badge
              color={PAYMENT_STATUS_CONFIG.scheduled.color}
              variant={PAYMENT_STATUS_CONFIG.scheduled.variant}
              size="sm"
            >
              {summary.counts.scheduled} {PAYMENT_STATUS_CONFIG.scheduled.label}
            </Badge>
          )}
          {/* MED-1286 (AC3): the failed value has to read as money. A bare count
              left a CM with no idea how much was outstanding. */}
          {summary.counts.failed > 0 && (
            <Badge color="dangerRed" variant="light" size="sm">
              {summary.counts.failed} Failed &middot; {formatCents(summary.total_failed_cents)}
            </Badge>
          )}
          {summary.counts.voided > 0 && (
            <Badge color="gray" variant="light" size="sm">
              {summary.counts.voided} Voided or cancelled &middot; {formatCents(summary.total_voided_cents)}
            </Badge>
          )}
        </Group>
      )}
    </Paper>
  );
}
